import { IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import ListView from "../../../components/common/ListView";
import formatDate, { formatDateTime } from "../../../utilities/helper";
import {
  useApprovedMutation,
  useFetchLineHaulRequestById,
} from "../../../apis/fleetApis";
import { usePermission } from "../../../hooks/usePermissionTabs";
import toast from "react-hot-toast";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  request?: any;
  onApproved?: () => void; // 👈 parent can refresh after approval
};

const TwoCol = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <p className="text-sm text-[#1E293B] flex items-center gap-1">
    <span className="font-medium text-[#212121]">{label}:</span> {value}
  </p>
);

const RequestViewApproveModal = ({
  open,
  onClose,
  request,
  onApproved,
}: Props) => {
  const { t, language } = useLanguage();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { allowed: canUpdateLinehaulInvoices } = usePermission("linehaul_invoices", "update");

  const { data } = useFetchLineHaulRequestById(1, 10, "", request?.id || "");
  const { mutate: approveRequest, isPending } = useApprovedMutation();

  useEffect(() => {
    if (open && request) {
      if (request.status === "APPROVE" && request.approvedFormId) {
        setSelectedIds([request.approvedFormId]);
      } else {
        setSelectedIds([]);
      }
    } else {
      setSelectedIds([]);
    }
  }, [open, request]);

  const handleApprove = () => {
    if (!canUpdateLinehaulInvoices) {
      toast.error(t("You do not have permission to approve linehaul request"));
      return;
    }
    if (!request?.id || selectedIds.length === 0) return;

    const payload = {
      formId: selectedIds[0],
      linehaulRequestId: request.id,
    };

    approveRequest(payload, {
      onSuccess: () => {
        if (onApproved) onApproved();
        onClose();
      },
    });
  };

  if (!open) return null;

  const toggleSelect = (id: number) => {
    setSelectedIds([id]);
  };

  return (
    <div 
      dir={language === "ar" ? "rtl" : "ltr"}
      className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4"
    >
      <div className="rounded-2xl w-8/12 relative">
        <div className="flex items-center">
          <div className="bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4 w-full rounded-tl-3xl rounded-tr-[63px]">
            <h1 className="text-2xl text-right flex-1">
              {t("Linehaul Request")} ({request?.requestId})
            </h1>
          </div>
          <button
            onClick={onClose}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
          >
            <IconX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="w-full py-4 px-6 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
          <div className="h-[calc(100vh-13rem)] overflow-y-auto space-y-4">
            <div className="space-y-3 border p-2 border-[#d2d2d2] rounded-[10px]">
              <h3 className="text-[#212121]/70 font-medium text-base">
                {t("Request Info")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4">
                <TwoCol label={t("Origin")} value={request.originWarehouse} />
                <TwoCol
                  label={t("Destination")}
                  value={request.destinationWarehouse}
                />
                <TwoCol label={t("Creator")} value={request.createdBy || "—"} />
                <TwoCol
                  label={t("Requested Date")}
                  value={formatDate(request.created_at)}
                />
                <TwoCol
                  label={t("Capacity")}
                  value={request.weightCapacity || "—"}
                />
                <TwoCol label={t("Email")} value={request.fromEmail} />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-[#212121]/70 font-medium text-base">
                {t("Companies Quotation")}
              </h3>
              <ListView
                mailDivClassName="w-full p-0 border rounded-[10px]"
                mainDivClassName=""
                trClassName="hover:bg-[#FBD037]/20 group"
                tdClassName="rounded-r-none rounded-l-none"
                data={data?.results ?? []}
                columns={[
                  {
                    label: t("Company Name"),
                    render: (c: any) => {
                      const isApproved =
                        request?.status === "APPROVE" &&
                        request?.approvedFormId === c.id;

                      return (
                        <div className="flex items-center gap-2">
                          <label className="relative flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isApproved || selectedIds.includes(c.id)}
                              onChange={() => toggleSelect(c.id)}
                              disabled={
                                isApproved || request?.status === "APPROVE" || !canUpdateLinehaulInvoices
                              }
                              className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                                bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                            />
                            <span
                              className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                            >
                              ✓
                            </span>
                          </label>
                          <span>{c.companyName}</span>
                        </div>
                      );
                    },
                  },
                  { label: t("Vehicle"), render: (c: any) => c.vehicleNumber },
                  { label: t("Origin"), render: (c: any) => c.originWarehouse },
                  {
                    label: t("Destination"),
                    render: (c: any) => c.destinationWarehouse,
                  },
                  {
                    label: t("Date & Time"),
                    render: (c: any) => formatDateTime(c.created_at),
                  },
                  { label: t("Amount"), render: (c: any) => c.amount },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px]">
          <button
            onClick={handleApprove}
            disabled={
              isPending ||
              selectedIds.length === 0 ||
              request?.status === "APPROVE" ||
              !canUpdateLinehaulInvoices
            }
            className="px-4 py-2 bg-[#FFD74F] text-sm font-medium rounded-full disabled:opacity-50"
          >
            {isPending ? t("Approving...") : t("Approve")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestViewApproveModal;
